// ── AllChecklistPage ──
import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { exportCSV, fmtDate, localFilter } from "../../../shared/format";
import { Badge, Btn, Card, ErrorMsg, Icon, PageLink, Pagination, STh, SearchBar, SectionHeader, Spinner, Td, TdNum, Th } from "../../../shared/ui";
import { usePagination, useSortable } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";

export const AllChecklistPage = () => {
    const { api, role, view, tab, setModal, setConfirm, delRec } = useApp();
    const [rows,setRows]=useState([]); const [loading,setLoading]=useState(true); const [err,setErr]=useState(null);
    const [q,setQ]=useState("");
    const [statusFilter,setStatusFilter]=useState("open");
    const load=()=>{setLoading(true);api.get("getClosingChecklist?all=1").then(d=>{setRows(Array.isArray(d)?d:(d.items||[]));setLoading(false);}).catch(e=>{setErr(e.message);setLoading(false);});};
    useEffect(()=>{load();},[]);

    const accrualRows = rows.filter(r => (r.loanstatus || "").toLowerCase().includes("accrual"));
    const catIsPost   = r => (r.checklistcategory || "").toLowerCase().includes("post close");

    const statusFiltered =
        statusFilter === "open"     ? accrualRows.filter(r => !r.completeddate)
      : statusFilter === "complete" ? accrualRows.filter(r =>  r.completeddate)
      : accrualRows;

    const filtered = localFilter(statusFiltered, q, ["companyname","accountnumber","loannumber","term_type","checklistcategory","checklistitem","responsibleparty","completedby"]);
    const { sorted, sortKey, sortDir, toggle } = useSortable(filtered, 'companyname');
    const { paginated:displayed, page, setPage, totalPages, total, reset } = usePagination(sorted, 100);

    const openCount = accrualRows.filter(r=>!r.completeddate).length;
    const doneCount = accrualRows.length - openCount;

    const [grpOpen, setGrpOpen] = useState({ pre:true, post:true, unfunded:true });
    const toggleGrp = k => setGrpOpen(g => ({ ...g, [k]: !g[k] }));

    const renderItemRow = (it) => {
      const needsDoc = it.documentrequired && !it.unrequiredocument;
      return (
        <tr key={it.id} style={{ cursor:"pointer" }} onClick={()=>setModal({type:"checklistitem-view",rec:it})} onMouseEnter={e=>e.currentTarget.style.background="var(--color-background-secondary)"} onMouseLeave={e=>e.currentTarget.style.background=""}>
          <Td style={{ fontWeight:500 }}>{it.companyname||"—"}</Td>
          <Td onClick={e=>e.stopPropagation()}>
            {it.loanid
              ? <PageLink page="loan" params={{accountid:it.accountid,loanid:it.loanid,companyname:it.companyname,loannumber:it.loannumber}} style={{ fontFamily:"var(--font-mono)", fontSize:12, color:B.cyan }}>{it.loannumber}</PageLink>
              : <span style={{ fontFamily:"var(--font-mono)", fontSize:12 }}>{it.loannumber||"—"}</span>}
          </Td>
          <Td>{it.term_type||"—"}</Td>
          <Td>{fmtDate(it.fundingdate)}</Td>
          <TdNum>{it.order??"—"}</TdNum>
          <Td>{it.checklistcategory||"—"}</Td>
          <Td style={{ fontWeight:500 }}>{it.checklistitem||"—"}</Td>
          <Td>{it.responsibleparty||"—"}</Td>
          <Td>{it.unrequiredocument ? <span style={{ color:"var(--color-text-secondary)" }}>waived</span> : (it.documentrequired?"✓":"—")}</Td>
          <Td>{it.completeddate ? <Badge s="Complete" /> : <span style={{ color:"var(--color-text-secondary)" }}>—</span>}</Td>
          <Td>{it.completedby||"—"}</Td>
          <Td onClick={e=>e.stopPropagation()}>
            {it.boxURL ? <a href={it.boxURL} target="_blank" rel="noreferrer" style={{ color:B.cyan }}>Link</a>
              : needsDoc ? <span style={{ color:B.amberDark, fontSize:11 }}>required</span> : "—"}
          </Td>
          <Td><div style={{ display:"flex",gap:6, justifyContent:"flex-end" }} onClick={e=>e.stopPropagation()}>
            <Btn onClick={()=>setModal({type:"checklistitem-view",rec:it})}><Icon type="view" /></Btn>
            {role.canEdit&&<Btn onClick={()=>setModal({type:"checklistitem-edit",rec:it,onDone:load})}><Icon type="edit" /></Btn>}
            {role.canDelete&&<Btn onClick={()=>setConfirm({msg:"Delete this checklist item?",onConfirm:()=>delRec("ClosingChecklistItems",it.id,"Deleted checklist item",load)})} variant="danger"><Icon type="delete" /></Btn>}
          </div></Td>
        </tr>
      );
    };

    // loan-number groups for a subset; kp keeps loan-header keys unique across sections
    const renderLoanGroups = (subset, kp) =>
      [...new Set(subset.map(r=>r.loannumber))].map(ln=>{
        const grp = subset.filter(r=>r.loannumber===ln);
        const head = grp[0];
        return [
          <tr key={kp+"-hdr-"+ln}>
            <td colSpan={99} style={{ padding:"8px 12px", fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", color:B.cyanDark, background:B.cyanLight }}>
              <span style={{ fontFamily:"var(--font-mono)" }}>{ln||"—"}</span>
              <span style={{ marginLeft:8, fontWeight:400 }}>— {head.companyname||"—"}</span>
              {head.loanstatus && <span style={{ marginLeft:8 }}><Badge s={head.loanstatus} /></span>}
              <span style={{ marginLeft:8, fontWeight:400, color:B.cyanDark }}>{grp.length} item{grp.length!==1?"s":""}</span>
            </td>
          </tr>,
          ...grp.map(renderItemRow)
        ];
      });

    // collapsible section band (Open tab only)
    const renderSection = (key, label, secRows) => secRows.length===0 ? [] : [
      <tr key={"sec-"+key}>
        <td colSpan={99} onClick={()=>toggleGrp(key)}
          style={{ padding:"9px 12px", fontSize:12, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", color:"#fff", background:B.cyan, cursor:"pointer", userSelect:"none" }}>
          <span style={{ marginRight:8 }}>{grpOpen[key]?"▼":"▶"}</span>{label}
          <span style={{ marginLeft:8, fontWeight:400, opacity:0.9 }}>({secRows.length})</span>
        </td>
      </tr>,
      ...(grpOpen[key] ? renderLoanGroups(secRows, key) : [])
    ];

    if(loading) return <Spinner />; if(err) return <ErrorMsg msg={err} onRetry={load} />;
    return <>
      <SectionHeader actions={[
        <SearchBar key="s" value={q} onChange={v=>{ setQ(v); reset(); }} placeholder="Search checklist items..." />,
        <Btn key="ex" onClick={()=>exportCSV(sorted,["companyname","accountnumber","loannumber","term_type","fundingdate","checklistcategory","checklistitem","responsibleparty","documentrequired","unrequiredocument","completeddate","completedby","boxURL"],"closing_checklist.csv",{ documentrequired:v=>v?"Yes":"No", unrequiredocument:v=>v?"Yes":"No" })}>Export CSV</Btn>
      ]} />

      <Card style={{ marginBottom:12, padding:12 }}>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
          {[
            ["open","Open",openCount],
            ["complete","Complete",doneCount],
            ["all","All",accrualRows.length],
          ].map(([key,label,count])=>(
            <button key={key} onClick={()=>{ setStatusFilter(key); reset(); }} style={{
              background: statusFilter===key ? B.cyan : "var(--color-background-secondary)",
              border: "0.5px solid "+(statusFilter===key ? B.cyan : "var(--color-border-tertiary)"),
              color: statusFilter===key ? "#fff" : "var(--color-text-secondary)",
              borderRadius:6, padding:"6px 12px", cursor:"pointer", fontSize:13,
              fontWeight: statusFilter===key ? 600 : 400
            }}>{label} <span style={{ opacity:0.8 }}>({count})</span></button>
          ))}
        </div>
      </Card>

      <Card style={{ overflowX:"auto" }}>
        <table style={{ width:"100%",borderCollapse:"collapse", whiteSpace:"nowrap" }}>
          <thead><tr>
            <STh label="Account"      sortKey="companyname"       currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="Loan #"       sortKey="loannumber"        currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="Term Type"    sortKey="term_type"         currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="Funding Date" sortKey="fundingdate"       currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="Order"        sortKey="order"             currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
            <STh label="Category"     sortKey="checklistcategory" currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="Item"         sortKey="checklistitem"     currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="Resp."        sortKey="responsibleparty"  currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <Th>Doc Req'd</Th>
            <STh label="Completed"    sortKey="completeddate"     currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="Completed By" sortKey="completedby"       currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <Th>Document</Th>
            <Th></Th>
          </tr></thead>
          <tbody>
            {statusFilter === "open"
              ? [
                  ...renderSection("pre",  "Funded Pre-Closing", displayed.filter(r=>  r.fundingdate && !catIsPost(r))),
                  ...renderSection("post", "Funded Post Close",  displayed.filter(r=>  r.fundingdate &&  catIsPost(r))),
                  ...renderSection("unfunded", "Open — Unfunded", displayed.filter(r=> !r.fundingdate)),
                ]
              : renderLoanGroups(displayed, "l")}
          </tbody>
        </table>
        <Pagination page={page} totalPages={totalPages} total={total} pageSize={100} onPage={setPage} />
      </Card>
    </>;
  };
